import {
  BaseSyntheticEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Loader } from "../../components/ui/Loader";
import { DataTable } from "primereact/datatable";
import { TableHeader } from "../../components/ui/table-header";
import { Column } from "primereact/column";
import { InputText } from "primereact/inputtext";
import { MultiSelect } from "primereact/multiselect";
import { useToast } from "../../components/ui/toast-context-provider";
import { useQueryClient } from "react-query";
import {
  CustomModal,
  CustomModalProps,
} from "../../components/ui/MobileModal/custom-modal";
import { FilterMatchMode } from "primereact/api";
import { useTreatmentTypeQuery } from "../../queries/veterinary-treatment-types.query";
import {
  useAddTreatmentMutation,
  useDeleteTreatmentMutation,
  useEditTreatmentMutation,
  useTreatmentsQuery,
} from "../../queries/treatments.query";
import { Treatment } from "../../queries/models/treatment.model";
import { TreatmentsForm } from "./TreatmentForm";
import { HorseTreatment } from "../../queries/models/horse-treatment.model";
import { format } from "date-fns";
import { useParams } from "react-router-dom";
import FormikImageFileUpload from "../../components/ui/formik/FormikFilesUpload";
import { useActiveHorseGroupsAsSelectOptionQuery } from "../../queries/horse-groups.query";

export function TreatmentsPage() {
  const queryClient = useQueryClient();
  const typesQuery = useTreatmentTypeQuery();
  const treatmentsQuery = useTreatmentsQuery();
  const addTypeMutation = useAddTreatmentMutation();
  const editTypeMutation = useEditTreatmentMutation();
  const deleteTypeMutation = useDeleteTreatmentMutation();
  const toast = useToast();
  const [selectedEntity, setSelectedEntity] = useState<Treatment>();
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [isDeleteConfirmationModalOpen, setIsDeleteConfirmationModalOpen] =
    useState<boolean>(false);
  const [globalFilterValue, setGlobalFilterValue] = useState("");

  const [filters, setFilters] = useState({
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
  });
  const [selectedHorseGroups, setSelectedHorseGroups] = useState<number[]>([]);
  const horseGroupOptionsQuery = useActiveHorseGroupsAsSelectOptionQuery();

  const params = useParams();
  const queryId = useMemo(
    () => (params?.id ? Number(params.id) : undefined),
    [params]
  );
  useEffect(() => {
    if (queryId) {
      const rider = treatmentsQuery.data?.find((x) => x.id === queryId);

      if (rider) {
        setSelectedEntity(rider);
        setIsModalOpen(true);
      }
    }
  }, [queryId, treatmentsQuery.data]);

  const handleGlobalFilterChange = useCallback(
    (e: BaseSyntheticEvent) => {
      const value = e.target.value;
      let _filters = filters;
      _filters.global.value = value;

      setFilters(_filters);
      setGlobalFilterValue(value);
    },
    [filters]
  );

  const handleAddNewEntity = () => {
    setSelectedEntity({
      comment: "",
      id: 0,
      date: new Date(),
      treatmentTypeId: 0,
    } as Treatment);
  };

  const handleSaveEntity = (data: Treatment) => {
    return data?.id ? editExistingEntity(data) : saveNewEntity(data);
  };

  const saveNewEntity = (data: Treatment) => {
    setIsSubmitting(true);
    const mutateOptions = {
      onSuccess: async () => {
        setSelectedEntity(undefined);
        toast.current?.show({
          severity: "success",
          detail: "Zapisano!",
        });
        await queryClient.invalidateQueries();
      },
      onError: async (error: any) => {
        toast.current?.show({
          severity: "error",
          detail: error.message,
        });
      },
      onSettled: () => {
        setIsSubmitting(false);
        setIsModalOpen(false);
      },
    };

    const request: Treatment = {
      id: data.id,
      comment: data.comment,
      date: data.date,
      treatmentTypeId: data.treatmentTypeId,
      horseTreatments: data.horseTreatments,
      newFiles: data.newFiles,
    };

    return addTypeMutation.mutateAsync(request, mutateOptions);
  };

  const editExistingEntity = (data: Treatment) => {
    setIsSubmitting(true);
    const mutateOptions = {
      onSuccess: async () => {
        setSelectedEntity(undefined);
        toast.current?.show({
          severity: "success",
          detail: "Zapisano!",
        });
        await queryClient.invalidateQueries();
      },
      onError: async (error: any) => {
        toast.current?.show({
          severity: "error",
          detail: error.message,
        });
      },
      onSettled: () => {
        setIsSubmitting(false);
        setIsModalOpen(false);
      },
    };

    const request: Treatment = {
      id: data.id,
      comment: data.comment,
      date: data.date,
      treatmentTypeId: data.treatmentTypeId,
      horseTreatments: data.horseTreatments,
      newFiles: data.newFiles,
    };

    return editTypeMutation.mutateAsync(request, mutateOptions);
  };

  const mobileTypeFormModalProps: CustomModalProps = {
    header: selectedEntity?.id ? "Edytuj zabieg" : "Dodaj zabieg",
    onClose: () => {
      setSelectedEntity(undefined);
      setIsModalOpen(false);
    },
    isOpen: isModalOpen,
    body: (
      <TreatmentsForm
        treatment={selectedEntity ?? ({} as Treatment)}
        onSave={handleSaveEntity}
        onCancel={() => {
          setSelectedEntity(undefined);
          setIsModalOpen(false);
        }}
        onDelete={() => setIsDeleteConfirmationModalOpen(true)}
        isSubmitting={isSubmitting}
      />
    ),
  };

  const handleDeleteType = useCallback(() => {
    if (selectedEntity) {
      deleteTypeMutation.mutateAsync(selectedEntity.id, {
        onSuccess: async () => {
          await queryClient.invalidateQueries();
          toast.current?.show({
            severity: "success",
            detail: "Zapisano!",
          });
          setSelectedEntity(undefined);
          setIsDeleteConfirmationModalOpen(false);
          setIsModalOpen(false);
        },
        onError: async (error: any) => {
          toast.current?.show({
            severity: "error",
            detail: error.message,
          });
        },
      });
    }
  }, [deleteTypeMutation, queryClient, selectedEntity, toast]);

  const mobileModalConfirmationFormProps: CustomModalProps = {
    header: "Potwierdź",
    body: "Czy chcesz usunąć?",
    height: "160px",
    width: "50%",
    isOpen: isDeleteConfirmationModalOpen,
    confirmation: true,
    onClose: () => setIsDeleteConfirmationModalOpen(false),
    centered: true,
    justified: true,
    onConfirm: handleDeleteType,
  };

  return (
    <>
      {typesQuery.isLoading ? (
        <div className="h-full flex align-items-center">
          <Loader type="spinner" />
        </div>
      ) : (
        <div className="h-full">
          {!!mobileTypeFormModalProps && (
            <CustomModal {...mobileTypeFormModalProps} />
          )}
          {!!mobileModalConfirmationFormProps && (
            <CustomModal {...mobileModalConfirmationFormProps} />
          )}
          <div className="p-1 ">
            <InputText
              className="w-full"
              placeholder="Filtr globalny"
              value={globalFilterValue}
              onChange={handleGlobalFilterChange}
            />
          </div>

          <div className="p-1">
            <MultiSelect
              value={selectedHorseGroups}
              onChange={(e) => setSelectedHorseGroups(e.value)}
              options={horseGroupOptionsQuery.data ?? []}
              optionLabel="label"
              display="chip"
              placeholder="Filtruj grupy koni"
              maxSelectedLabels={2}
              className="w-full md:w-20rem mb-2"
            />
          </div>

          <div style={{ height: "calc(100% - 3rem)" }}>
            <DataTable
              value={treatmentsQuery.data?.filter(t =>
                selectedHorseGroups.length === 0
                  ? true
                  : (t.horseTreatments ?? []).some(ht =>
                      ((ht.horse as any)?.horseGroups ?? []).some((g: any) => selectedHorseGroups.includes(g.id))
                    )
              )}
              selectionMode="single"
              selection={selectedEntity}
              onSelectionChange={(e) => {
                setSelectedEntity(e.value as Treatment);
                setIsModalOpen(true);
              }}
              filters={filters}
              globalFilterFields={["name", "comment"]}
              header={
                <TableHeader
                  header="Zabiegi"
                  showAddButton={true}
                  onClickAdd={() => {
                    handleAddNewEntity();
                    setIsModalOpen(true);
                  }}
                />
              }
              style={{ width: "100%" }}
              tableStyle={{ tableLayout: "fixed" }}
            >
              <Column
                field="date"
                header="Data"
                sortable
                style={{ width: "30%" }}
                body={(rowData) =>
                  rowData.date
                    ? format(new Date(rowData.date), "dd/MM/yyyy HH:mm")
                    : ""
                }
              />

              <Column
                field="treatmentType"
                header="Typ"
                sortable
                style={{ width: "30%" }}
                body={(rowData) => rowData.treatmentType?.name}
              />

              <Column
                field="horseTreatments"
                header="Konie"
                sortable
                style={{ width: "40%" }}
                body={(rowData) =>
                  rowData.horseTreatments
                    ?.map((ht: HorseTreatment) => ht.horse?.name)
                    .join(", ")
                }
                className="white-space-normal text-wrap text-sm"
              />
            </DataTable>
          </div>
        </div>
      )}
    </>
  );
}
